import { resolveCsrfToken, sendJsonMutation } from '../../components/write-request.js';
import { canAnimateElement, readCssTimeMs } from '../../components/motion-policy.js';
import { cancelAnimations, playAnimation, settleAnimations } from '../../components/motion-runner.js';
import { isRankingBusy, isRankingStale, markRankingStale, setRankingBusy } from './busy-lock.js';

const REORDER_FAILURE_MESSAGE = 'Could not save the new order. Try again.';
const REORDER_REFRESH_FAILURE_MESSAGE = 'Order saved, but the screen could not be refreshed. Reload the page.';

/**
 * Moves one entry of a list to another index and returns a new list. The single piece of ordering
 * logic behind drag-to-reorder: the DOM is rewritten from its result, so the rest of the module stays
 * mechanical.
 */
export function moveItem(list, fromIndex, toIndex) {
    const items = [...list];
    if (
        !Number.isInteger(fromIndex)
        || !Number.isInteger(toIndex)
        || fromIndex < 0
        || toIndex < 0
        || fromIndex >= items.length
        || toIndex >= items.length
        || fromIndex === toIndex
    ) {
        return items;
    }

    const [moved] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, moved);

    return items;
}

/**
 * `showStatus` is injected rather than resolved here: the editing screen has one save-status surface and
 * one owner for it, so a drag and a placement cannot report the same class of event in two places and in
 * two wordings.
 */
export function initOrderedReorder(root = globalThis.document, fetchImpl = globalThis.fetch, options = {}) {
    const slotsRoot = root?.querySelector?.('[data-rankings-positions]');
    if (!(slotsRoot instanceof HTMLElement)) {
        return null;
    }

    const reorderUrl = String(slotsRoot.dataset.rankingsReorderUrl ?? '').trim();
    if (reorderUrl === '') {
        return null;
    }

    const refreshAfterSave = options.refreshAfterSave ?? null;
    const showStatus = options.showStatus ?? (() => {});
    // Every control that mutates this ranking by position. While an optimistic order is unconfirmed the
    // visible positions are not the stored ones, so submitting any of them would act on the wrong slot.
    const mutationControls = [
        ...slotsRoot.querySelectorAll('button'),
        ...root.querySelectorAll('[data-rankings-assign-form] button'),
    ];
    let rows = [...slotsRoot.querySelectorAll('[data-rankings-slot]')];
    let rowsBeforeDrag = [...rows];
    let draggedRow = null;
    let didReorder = false;
    let reorderAnimations = [];
    let reorderAnimationGeneration = 0;
    let reorderMotion = Promise.resolve();
    // This module's own optimistic-save state, deliberately separate from the shared lock: it decides
    // whether the visible order is confirmed, which is a fact about this list and not about the page. Tying
    // the button state to the shared lock would leave the arrows disabled after every stored operation,
    // because a swapped-in list is re-initialised while the operation that swapped it still holds the lock.
    let saveInFlight = false;

    const setMutationsLocked = (locked) => {
        slotsRoot.classList.toggle('is-locked', locked);
        mutationControls.forEach((control) => {
            control.disabled = locked;
        });

        if (!locked) {
            // Unlocking enables every control, including the move buttons the list bounds disable.
            renumber();
        }
    };

    const collectRowPositions = () => new Map(rows.map((row) => [
        row,
        row.getBoundingClientRect?.().top,
    ]));

    const animateRowOrder = (previousPositions, movedRow, interactive) => {
        cancelAnimations(reorderAnimations);
        reorderAnimations = [];
        rows.forEach((row) => row.removeAttribute('data-rankings-reorder-active'));
        const generation = ++reorderAnimationGeneration;

        rows.forEach((row) => {
            const previousTop = previousPositions.get(row);
            const currentTop = row.getBoundingClientRect?.().top;
            const delta = previousTop - currentTop;
            // During native drag the browser already moves a pointer ghost. Animating the source row too
            // makes it visibly chase that ghost; only the neighbours need to make room.
            if (
                !Number.isFinite(delta)
                || delta === 0
                || (interactive && row === movedRow)
                || !canAnimateElement(row)
            ) {
                return;
            }

            const moved = row === movedRow;
            if (moved) {
                row.setAttribute('data-rankings-reorder-active', '');
            }
            const keyframes = interactive ? [
                { transform: `translateY(${delta}px)` },
                { transform: 'translateY(0)' },
            ] : moved ? [
                { opacity: 1, transform: `translateY(${delta}px) scale(1)` },
                { opacity: 1, offset: 0.55, transform: `translateY(${delta * 0.45}px) scale(1.012)` },
                { opacity: 1, transform: 'translateY(0) scale(1)' },
            ] : [
                { opacity: 1, transform: `translateY(${delta}px)` },
                { opacity: 0.72, offset: 0.55, transform: `translateY(${delta * 0.45}px)` },
                { opacity: 1, transform: 'translateY(0)' },
            ];
            const animation = playAnimation(row, keyframes, {
                duration: readCssTimeMs(
                    row,
                    interactive ? '--rankings-drag-reorder-duration' : '--rankings-reorder-duration',
                    interactive ? 140 : 400,
                ),
                easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
                fill: 'both',
            });
            if (animation !== null) {
                reorderAnimations.push(animation);
            }
        });

        const animations = [...reorderAnimations];
        reorderMotion = settleAnimations(animations).then(() => {
            if (generation !== reorderAnimationGeneration) {
                return;
            }

            cancelAnimations(animations);
            reorderAnimations = [];
            rows.forEach((row) => row.removeAttribute('data-rankings-reorder-active'));
        });
    };

    const moveRow = (row, toIndex) => {
        const fromIndex = rows.indexOf(row);
        if (
            !(row instanceof HTMLElement)
            || saveInFlight
            || isRankingBusy(root)
            || fromIndex < 0
            || !Number.isInteger(toIndex)
            || toIndex < 0
            || toIndex >= rows.length
            || fromIndex === toIndex
        ) {
            return false;
        }

        rowsBeforeDrag = [...rows];
        const previousPositions = collectRowPositions();
        rows = moveItem(rows, fromIndex, toIndex);
        applyRowOrder();
        animateRowOrder(previousPositions, row, false);
        void persist();

        return true;
    };

    const applyRowOrder = () => {
        // Re-appending a row restarts its entrance animation, so the list opts out of it for good the
        // first time JS rewrites the order.
        slotsRoot.classList.add('is-reordered');
        rows.forEach((row) => slotsRoot.append(row));
        renumber();
    };

    const renumber = () => {
        // Aiming the picker names a position, not a game, so a reorder does not change what is aimed at -
        // it changes which row sits there, and the marker has to follow the position.
        //
        // Read from the explicit choice, never from the hidden assign input: that input also carries the
        // fallback for a bare `Add`, so trusting it would paint a selection nobody made the moment a row
        // is dragged on a screen that was opened without one.
        const aimedPosition = Number.parseInt(
            String(root.querySelector('[data-rankings-edit]')?.dataset?.rankingsSelectedPosition ?? ''),
            10,
        );

        rows.forEach((row, index) => {
            const position = index + 1;
            row.dataset.position = String(position);

            const label = row.querySelector('.rankings-slot-position');
            if (label instanceof HTMLElement) {
                // Reordering exists only for `ordered`, whose numbers render as `#N`. Writing back a bare
                // number would silently restyle every position label the first time a row is dragged.
                label.textContent = '#' + position;
            }
            row.querySelectorAll('input[name="position"]').forEach((input) => {
                input.value = String(position);
            });
            // The number is also the picker's aiming control, and it addresses a position. An optimistic
            // order that renumbered the label but not the link would aim at the position this row used to
            // hold, and the next `Add` would land somewhere other than the row that was clicked.
            const isAimed = position === aimedPosition;
            row.classList.toggle('is-selected', isAimed);

            const select = row.querySelector('[data-rankings-select-position]');
            if (select instanceof HTMLElement) {
                select.dataset.rankingsSelectPosition = String(position);
                const href = select.getAttribute('href');
                if (typeof href === 'string') {
                    select.setAttribute('href', href.replace(/([?&]position=)\d+/, '$1' + position));
                }
                select.setAttribute('aria-label', 'Add a game to #' + position);
                select.setAttribute('aria-current', isAimed ? 'true' : 'false');
            }
            // The no-JS move controls address a target position, so an optimistic order has to move
            // their destinations with them - and re-close the two ends of the list.
            row.querySelectorAll('[data-rankings-move-delta]').forEach((button) => {
                const target = position + Number(button.dataset.rankingsMoveDelta ?? 0);
                button.value = String(target);
                button.disabled = saveInFlight || target < 1 || target > rows.length;
            });
        });
    };

    const persist = async () => {
        // Nothing else may be writing to this ranking: a placement still re-reading the screen would put
        // back the pre-drag order on top of an order that had already been stored.
        if (saveInFlight || isRankingBusy(root)) {
            return;
        }

        const rollbackRows = [...rowsBeforeDrag];
        saveInFlight = true;
        setRankingBusy(root, true);
        setMutationsLocked(true);
        showStatus('Saving...', 'saving');

        let result;
        let refreshed = 'ok';
        try {
            result = await sendJsonMutation(fetchImpl, {
                url: reorderUrl,
                csrfToken: resolveCsrfToken(root),
                body: { order: rows.map((row) => String(row.dataset.userGameId ?? '')) },
                fallbackErrorMessage: REORDER_FAILURE_MESSAGE,
            });
            if (result.ok && typeof refreshAfterSave === 'function') {
                // The request and motion run together, but a fast local response must not replace the rows
                // halfway through the movement.
                await reorderMotion;
                refreshed = await refreshAfterSave();
                if (refreshed !== 'ok') {
                    // The order was stored, but the picker beside it was not brought back in line: the
                    // permutation can have moved a filled row into the aimed position, and the next `Add`
                    // would then overwrite a game instead of filling a hole. Terminal until a reload.
                    markRankingStale(root);
                }
            }
        } finally {
            // The lock is shared, so leaving it held would not just wedge this list: it stops every other
            // write to the ranking and keeps the picker deferring its searches indefinitely. A stale screen
            // is the exception - there the controls stay shut, because there is no correct submit left.
            saveInFlight = false;
            setRankingBusy(root, false);
            setMutationsLocked(isRankingStale(root));
        }

        if (result.ok) {
            // A row that shifted by one does not look like something that reached the server, so this is
            // the operation the status earns its place for.
            if (refreshed === 'ok') {
                showStatus('Saved', 'saved');
            } else {
                showStatus(REORDER_REFRESH_FAILURE_MESSAGE, 'error');
            }

            return;
        }

        // The optimistic order was never stored, so it is put back exactly as it was before the drag.
        // Leaving it on screen would leave the visible positions - and the position inputs of the clear
        // and assign controls - pointing at slots the server does not have.
        rows = rollbackRows;
        applyRowOrder();
        showStatus(result.errorMessage, 'error');
    };

    slotsRoot.addEventListener('dragstart', (event) => {
        const row = event.target instanceof HTMLElement
            ? event.target.closest('[data-rankings-slot]')
            : null;
        // A drag that starts while something else is writing to this ranking ends up saving an order built
        // from rows that are about to be replaced by that write's re-read.
        if (!(row instanceof HTMLElement) || row.draggable !== true || saveInFlight || isRankingBusy(root)) {
            return;
        }

        draggedRow = row;
        didReorder = false;
        rowsBeforeDrag = [...rows];
        row.classList.add('is-dragging');
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', String(row.dataset.position ?? ''));
        }
    });

    slotsRoot.addEventListener('dragover', (event) => {
        if (!(draggedRow instanceof HTMLElement)) {
            return;
        }

        event.preventDefault();
        const overRow = event.target instanceof HTMLElement
            ? event.target.closest('[data-rankings-slot]')
            : null;
        if (!(overRow instanceof HTMLElement) || overRow === draggedRow) {
            return;
        }

        const fromIndex = rows.indexOf(draggedRow);
        const toIndex = rows.indexOf(overRow);
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
            return;
        }

        const overBounds = overRow.getBoundingClientRect?.();
        const overMiddle = Number(overBounds?.top) + (Number(overBounds?.height) / 2);
        if (
            Number.isFinite(event.clientY)
            && Number.isFinite(overMiddle)
            && ((fromIndex > toIndex && event.clientY >= overMiddle)
                || (fromIndex < toIndex && event.clientY <= overMiddle))
        ) {
            return;
        }

        const previousPositions = collectRowPositions();
        rows = moveItem(rows, fromIndex, toIndex);
        applyRowOrder();
        animateRowOrder(previousPositions, draggedRow, true);
        didReorder = true;
    });

    slotsRoot.addEventListener('dragend', () => {
        if (!(draggedRow instanceof HTMLElement)) {
            return;
        }

        draggedRow.classList.remove('is-dragging');
        draggedRow = null;
        if (didReorder) {
            didReorder = false;
            void persist();
        }
    });

    return { persist, moveRow };
}
